import Vue from 'vue'
import VueRouter from 'vue-router'
import { canNavigate, needReAuth } from '@/libs/acl/routeProtection'
import { isUserLoggedIn } from '@/auth/utils'
import pages from '@/router/routes/pages'
import reports from '@/router/routes/reports'
import patients from '@/router/routes/patients'
import dictionary from '@/router/routes/dictionary'
import application from '@/router/routes/application'
import schedule from '@/router/routes/schedule'
import dentalChat from '@/router/routes/dental-chat'
import notifications from '@/router/routes/notifications'
import wage from '@/router/routes/wage'
import lead from '@/router/routes/lead'
import faq from '@/router/routes/faq'
import serviceCalculation from '@/router/routes/serviceCalculation'
import dentalLaboratory from '@/router/routes/dentalLaboratory'
import todo from '@/router/routes/todo'
import revenue from '@/router/routes/revenue'
import analytics from '@/router/routes/analytics'
import integrator from '@/router/routes/integrator'
import i18n from '@/libs/i18n'
import { getProgramVersionName } from '@/composables/currentUser'
import store from '@/store'
import useApp from '@/composables/app'
import useWindow from '@/composables/window'
import appConstants from '@/constants/constants'
import useAuth from '@/composables/auth'

Vue.use(VueRouter)

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes: [
    { path: '/', redirect: { name: 'patient-cards' } },
    ...patients,
    ...dictionary,
    ...application,
    ...pages,
    ...reports,
    ...schedule,
    ...dentalChat,
    ...notifications,
    ...wage,
    ...lead,
    ...faq,
    ...serviceCalculation,
    ...dentalLaboratory,
    ...todo,
    ...revenue,
    ...analytics,
    ...integrator,
    {
      path: '*',
      redirect: 'error-404',
    },
  ],
})

const { goToHomePage } = useApp()
const { allowUseUserOnlineChecker, prohibitUseUserOnlineChecker } = useWindow()

router.beforeEach((to, _, next) => {
  if (['appointment-booking', 'appointment-booking-short-link'].includes(to.name)) {
    processTitle(to.meta.title)
    prohibitSocketConnection()
    prohibitUseUserOnlineChecker()
    return next()
  }

  const isLoggedIn = isUserLoggedIn()

  if (!canNavigate(to)) {
    prohibitSocketConnection()
    prohibitUseUserOnlineChecker()

    if (!isLoggedIn) return next({ name: 'auth-login' })

    return next({ name: 'error-404' })
  }

  if (to.name !== 'auth-login' && needReAuth()) {
    const { logout } = useAuth()
    logout(appConstants.auth.logoutReason.END_OF_SESSION)
    return
  }

  if (to.redirectedFrom === '/') {
    goToHomePage()
    return
  }

  // Redirect if logged in
  if (to.meta.redirectIfLoggedIn && isLoggedIn) {
    allowSocketConnection()
    allowUseUserOnlineChecker()
    goToHomePage()
    return
  }

  if (!isLoggedIn) {
    prohibitSocketConnection()
    prohibitUseUserOnlineChecker()
  } else {
    allowSocketConnection()
    allowUseUserOnlineChecker()
  }

  processTitle(to.meta.title)
  return next()
})

router.beforeResolve(async (to, _, next) => {
  if (
    to.meta
    && to.meta.validateParam
    && to.meta.validator
    && typeof to.meta.validator === 'function'
    && to.meta.validateParam in to.params
  ) {
    return to.meta.validator(to.params[to.meta.validateParam]) ? next() : next({ name: 'error-404' })
  }
  return next()
})

const processTitle = title => {
  let appName = getProgramVersionName()
  document.title = title ? `${i18n.t(title)} | ${appName}` : appName
}

const prohibitSocketConnection = () => store.commit('auth/SET_CAN_CONNECT_TO_SOCKET', false)
const allowSocketConnection = () => store.commit('auth/SET_CAN_CONNECT_TO_SOCKET', true)

export default router
